import { useState } from 'react';
import { api, errorMessage } from '../../lib/api.js';
import { useInvalidate, useMutation, useQuery, useQuota } from '../../lib/queries.js';
import {
  Badge, Button, Card, ConfirmDialog, ErrorBox, Input, Loading, PageHeader,
  Select, Tabs, Textarea, useToast, cx,
} from '../../components/ui/index.js';
import { PLAN_LABEL, num, date, relative } from '../../lib/format.js';
import { hasRole, useAuth, useRole } from '../../lib/auth.js';

interface Tenant {
  id: string; slug: string; companyName: string; legalName: string | null; taxId: string | null;
  websiteUrl: string | null; contactEmail: string | null; contactPhone: string | null;
  address: string | null; planCode: string; planExpiresAt: string | null;
  quota: { blogs: { used: number; max: number }; posts: { used: number; max: number };
           widgets: { used: number; max: number }; members: { used: number; max: number };
           storageMb: { used: number; max: number }; allowCustomDomain: boolean; showBranding: boolean };
}

interface SessionRow { id: string; userAgent: string | null; createdAt: string; expiresAt: string }

function ProfileTab() {
  const { user, setUser } = useAuth();
  const toast = useToast();
  const [form, setForm] = useState({ fullName: user?.fullName ?? '', locale: user?.locale ?? 'th' });

  const save = useMutation({
    mutationFn: () => api.patch<{ user: NonNullable<typeof user> }>('/v1/auth/me', form),
    onSuccess: (r) => { setUser(r.user); toast('บันทึกโปรไฟล์แล้ว'); },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  return (
    <Card className="max-w-xl space-y-4 p-5">
      <Input label="ชื่อ-นามสกุล" value={form.fullName}
             onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
      <Input label="อีเมล" value={user?.email ?? ''} disabled
             hint="ยังไม่รองรับการเปลี่ยนอีเมลด้วยตัวเอง — ติดต่อฝ่ายสนับสนุน" />
      <Select label="ภาษา" value={form.locale} onChange={(e) => setForm({ ...form, locale: e.target.value })}>
        <option value="th">ไทย</option>
        <option value="en">English</option>
      </Select>
      <Button loading={save.isPending} onClick={() => save.mutate()}>บันทึก</Button>
    </Card>
  );
}

function SecurityTab() {
  const toast = useToast();
  const [pw, setPw] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [revoking, setRevoking] = useState(false);

  const sessions = useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.get<{ data: SessionRow[] }>('/v1/auth/sessions').then((r) => r.data),
  });

  const change = useMutation({
    mutationFn: () => api.post('/v1/auth/change-password', {
      currentPassword: pw.currentPassword, newPassword: pw.newPassword,
    }),
    onSuccess: () => {
      toast('เปลี่ยนรหัสผ่านแล้ว — ระบบออกจากระบบทุกอุปกรณ์ กรุณาเข้าสู่ระบบใหม่');
      setTimeout(() => { window.location.href = '/login'; }, 1600);
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const revokeAll = useMutation({
    mutationFn: () => api.del('/v1/auth/sessions'),
    onSuccess: () => {
      toast('ออกจากระบบทุกอุปกรณ์แล้ว');
      setTimeout(() => { window.location.href = '/login'; }, 1200);
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const mismatch = pw.confirm.length > 0 && pw.newPassword !== pw.confirm;

  return (
    <div className="max-w-xl space-y-5">
      <Card className="space-y-4 p-5">
        <h3 className="text-sm font-semibold text-[var(--ink)]">เปลี่ยนรหัสผ่าน</h3>
        <Input label="รหัสผ่านปัจจุบัน" type="password" autoComplete="current-password"
               value={pw.currentPassword} onChange={(e) => setPw({ ...pw, currentPassword: e.target.value })} />
        <Input label="รหัสผ่านใหม่" type="password" autoComplete="new-password" minLength={10}
               hint="อย่างน้อย 10 ตัวอักษร"
               value={pw.newPassword} onChange={(e) => setPw({ ...pw, newPassword: e.target.value })} />
        <Input label="ยืนยันรหัสผ่านใหม่" type="password" autoComplete="new-password"
               error={mismatch ? 'รหัสผ่านทั้งสองช่องไม่ตรงกัน' : null}
               value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />
        <Button loading={change.isPending} disabled={mismatch || !pw.currentPassword || pw.newPassword.length < 10}
                onClick={() => change.mutate()}>เปลี่ยนรหัสผ่าน</Button>
      </Card>

      <Card className="p-5">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-[var(--ink)]">อุปกรณ์ที่เข้าสู่ระบบอยู่</h3>
          <Button size="sm" variant="outline" onClick={() => setRevoking(true)}>ออกจากระบบทุกอุปกรณ์</Button>
        </div>
        {sessions.isLoading ? <Loading label="" /> : (
          <ul className="divide-y divide-[var(--line-soft)] text-sm">
            {sessions.data?.map((s) => (
              <li key={s.id} className="py-2.5">
                <p className="truncate text-[var(--ink-2)]">{s.userAgent ?? 'อุปกรณ์ไม่ทราบชื่อ'}</p>
                <p className="text-xs text-[var(--ink-3)]">เข้าสู่ระบบ{relative(s.createdAt)} · หมดอายุ {date(s.expiresAt)}</p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="p-5">
        <h3 className="text-sm font-semibold text-[var(--ink)]">การยืนยันตัวตนสองชั้น (2FA)</h3>
        <p className="mt-1.5 text-sm text-[var(--ink-3)]">
          จะเปิดให้ใช้ใน Phase 2 — โครงสร้างฐานข้อมูลรองรับไว้แล้ว
        </p>
        <div className="mt-2"><Badge tone="amber">เร็ว ๆ นี้</Badge></div>
      </Card>

      <ConfirmDialog
        open={revoking} onClose={() => setRevoking(false)} onConfirm={() => revokeAll.mutate()}
        loading={revokeAll.isPending} danger confirmLabel="ออกจากระบบทุกอุปกรณ์"
        title="ออกจากระบบทุกอุปกรณ์?"
        message="ทุกอุปกรณ์รวมถึงเครื่องนี้จะถูกออกจากระบบ คุณต้องเข้าสู่ระบบใหม่"
      />
    </div>
  );
}

function CompanyTab() {
  const toast = useToast();
  const invalidate = useInvalidate();
  const role = useRole();
  const canEdit = hasRole(role, 'ADMIN');

  const tenant = useQuery({
    queryKey: ['tenant'],
    queryFn: () => api.get<{ data: Tenant }>('/v1/tenants/current').then((r) => r.data),
  });

  const [form, setForm] = useState<Partial<Tenant> | null>(null);
  const value = form ?? tenant.data ?? null;

  const save = useMutation({
    mutationFn: () => api.patch('/v1/tenants/current', {
      companyName: value?.companyName,
      legalName: value?.legalName || null,
      taxId: value?.taxId || null,
      websiteUrl: value?.websiteUrl || null,
      contactEmail: value?.contactEmail || null,
      contactPhone: value?.contactPhone || null,
      address: value?.address || null,
    }),
    onSuccess: () => { invalidate(['tenant']); toast('บันทึกข้อมูลบริษัทแล้ว'); },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  if (tenant.isLoading) return <Loading />;
  if (!value) return <ErrorBox error={tenant.error} />;
  const set = (patch: Partial<Tenant>) => setForm({ ...value, ...patch });

  return (
    <Card className="max-w-xl space-y-4 p-5">
      <Input label="ชื่อบริษัท" value={value.companyName ?? ''} disabled={!canEdit}
             onChange={(e) => set({ companyName: e.target.value })} />
      <Input label="ชื่อจดทะเบียน" value={value.legalName ?? ''} disabled={!canEdit}
             onChange={(e) => set({ legalName: e.target.value })} />
      <Input label="เลขประจำตัวผู้เสียภาษี" value={value.taxId ?? ''} disabled={!canEdit}
             maxLength={13} inputMode="numeric" hint="ตัวเลข 13 หลัก — ใช้ในใบกำกับภาษี"
             onChange={(e) => set({ taxId: e.target.value })} />
      <Input label="เว็บไซต์บริษัท" type="url" placeholder="https://company.co.th"
             value={value.websiteUrl ?? ''} disabled={!canEdit}
             onChange={(e) => set({ websiteUrl: e.target.value })} />
      <div className="grid gap-4 sm:grid-cols-2">
        <Input label="อีเมลติดต่อ" type="email" value={value.contactEmail ?? ''} disabled={!canEdit}
               onChange={(e) => set({ contactEmail: e.target.value })} />
        <Input label="เบอร์โทร" value={value.contactPhone ?? ''} disabled={!canEdit}
               onChange={(e) => set({ contactPhone: e.target.value })} />
      </div>
      <Textarea label="ที่อยู่" rows={3} value={value.address ?? ''} disabled={!canEdit}
                onChange={(e) => set({ address: e.target.value })} />
      {canEdit && <Button loading={save.isPending} onClick={() => save.mutate()}>บันทึก</Button>}
    </Card>
  );
}

function PlanTab() {
  const quota = useQuota();
  const tenant = useQuery({
    queryKey: ['tenant'],
    queryFn: () => api.get<{ data: Tenant }>('/v1/tenants/current').then((r) => r.data),
  });
  const plans = useQuery({
    queryKey: ['plans'],
    queryFn: () => api.get<{ data: any[] }>('/v1/plans').then((r) => r.data),
  });

  if (quota.isLoading || tenant.isLoading) return <Loading />;
  const q = quota.data;
  const current = tenant.data?.planCode ?? 'FREE';

  const rows: [string, { used: number; max: number }][] = q ? [
    ['บล็อก', q.blogs], ['บทความ', q.posts], ['Widget', q.widgets],
    ['สมาชิกทีม', q.members],
  ] : [];

  return (
    <div className="max-w-3xl space-y-5">
      <Card className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-[var(--ink-3)]">แพ็กเกจปัจจุบัน</p>
            <p className="mt-1 text-2xl font-bold text-[var(--ink)]">{PLAN_LABEL[current] ?? current}</p>
          </div>
          {q && (
            <div className="flex gap-2">
              {q.allowCustomDomain && <Badge tone="green">ใช้โดเมนตัวเองได้</Badge>}
              {!q.showBranding && <Badge tone="green">ไม่มีแบรนด์ Blogaris</Badge>}
            </div>
          )}
        </div>

        <div className="mt-5 space-y-3">
          {rows.map(([label, v]) => {
            const p = Math.min(100, Math.round((v.used / Math.max(1, v.max)) * 100));
            return (
              <div key={label}>
                <div className="flex justify-between text-sm">
                  <span className="text-[var(--ink-2)]">{label}</span>
                  <span className="tabular-nums text-[var(--ink-3)]">{num(v.used)} / {num(v.max)}</span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-[var(--sunken)]">
                  <div className={cx('h-full', p > 90 ? 'bg-rose-500' : p > 70 ? 'bg-amber-500' : 'bg-[var(--brand)]')}
                       style={{ width: `${p}%` }} />
                </div>
              </div>
            );
          })}
          {q && (
            <div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--ink-2)]">พื้นที่จัดเก็บ</span>
                <span className="tabular-nums text-[var(--ink-3)]">{num(q.storageMb.used)} / {num(q.storageMb.max)} MB</span>
              </div>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-[var(--sunken)]">
                <div className="h-full bg-[var(--brand)]"
                     style={{ width: `${Math.min(100, (q.storageMb.used / Math.max(1, q.storageMb.max)) * 100)}%` }} />
              </div>
            </div>
          )}
        </div>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {plans.data?.map((p) => (
          <Card key={p.code} className={cx('p-4', p.code === current && 'ring-2 ring-brand-500')}>
            <p className="text-sm font-bold text-[var(--ink)]">{p.name}</p>
            <p className="mt-1 text-xl font-bold text-[var(--brand)]">
              {p.priceThbMonth === 0 ? 'ฟรี' : `฿${num(p.priceThbMonth)}`}
              {p.priceThbMonth > 0 && <span className="text-xs font-normal text-[var(--ink-3)]">/เดือน</span>}
            </p>
            {/*
              * แสดงส่วนลดเป็น % ที่คำนวณสด ไม่ hardcode "15%"
              * ถ้าวันหลังมีคนแก้ราคาในตาราง plans แล้วลืมแก้ข้อความ ตัวเลขจะยังตรงเสมอ
              */}
            {p.priceThbYear > 0 && (
              <p className="mt-0.5 text-xs text-[var(--ink-3)]">
                หรือ <span className="font-semibold text-[var(--ink-2)]">฿{num(p.priceThbYear)}</span>/ปี
                {' '}<span className="text-[var(--brand)]">
                  (ประหยัด {Math.round((1 - p.priceThbYear / (p.priceThbMonth * 12)) * 100)}%)
                </span>
              </p>
            )}
            <p className="mt-1.5 text-xs text-[var(--ink-3)]">{p.description}</p>
            <ul className="mt-3 space-y-1 text-xs text-[var(--ink-2)]">
              <li>· {num(p.maxBlogs)} บล็อก</li>
              <li>· {num(p.maxPosts)} บทความ</li>
              <li>· {num(p.maxWidgets)} widget</li>
              <li>· {num(p.maxStorageMb)} MB</li>
              {p.allowCustomDomain && <li>· โดเมนของคุณเอง</li>}
              {!p.showBranding && <li>· ไม่มีแบรนด์ Blogaris</li>}
            </ul>
            {p.code === current
              ? <Badge tone="blue" className="mt-3">แพ็กเกจปัจจุบัน</Badge>
              : <p className="mt-3 text-xs text-[var(--ink-3)]">ติดต่อฝ่ายขายเพื่ออัปเกรด</p>}
          </Card>
        ))}
      </div>

      <p className="text-xs text-[var(--ink-3)]">
        ราคาทั้งหมด<strong className="text-[var(--ink-2)]"> รวม VAT 7% แล้ว</strong> ·
        จ่ายรายปีประหยัดกว่าจ่ายรายเดือน 12 ครั้ง
      </p>
      <p className="text-xs text-[var(--ink-3)]">
        การชำระเงินออนไลน์ยังไม่เปิดใช้งาน — ระบบชำระเงิน (Omise / Stripe) อยู่ในแผน Phase 2 ตามเอกสารออกแบบ
      </p>
    </div>
  );
}

export function SettingsPage() {
  const [tab, setTab] = useState('profile');
  return (
    <>
      <PageHeader title="ตั้งค่า" />
      <Tabs active={tab} onChange={setTab} tabs={[
        { id: 'profile', label: 'โปรไฟล์' },
        { id: 'security', label: 'ความปลอดภัย' },
        { id: 'company', label: 'บริษัท' },
        { id: 'plan', label: 'แพ็กเกจ' },
      ]} />
      <div className="mt-6">
        {tab === 'profile' && <ProfileTab />}
        {tab === 'security' && <SecurityTab />}
        {tab === 'company' && <CompanyTab />}
        {tab === 'plan' && <PlanTab />}
      </div>
    </>
  );
}
